/**
 * What an attached document may write onto the supplier document dialog.
 *
 * Phase 25.2 (PRD 13.3). Pure decision logic, deliberately free of React and
 * of any component import: it is the sole guard on Principle 2 for this
 * feature, it is pinned by supplier-prefill.test.js, and a rule this load-
 * bearing should be findable on its own rather than buried in a 3000-line
 * component.
 */

// Mirrors the currency list used by day-receipt-form.jsx for expense receipts.
// Lives here rather than in the page because it is two facts at once — what
// the Select offers, and what a document is allowed to prefill — and they must
// never drift apart.
export const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP'];

// Which fields the invoice dialog will accept from an attached document.
//
// `doc_type` is deliberately absent even though the extractor returns it:
// the dialog only ever creates an invoice (KD-093), and the guess is used
// solely for the intake screen's "this looks like a receipt" hint.
//
// `org_number` is absent because the form has no such field — it exists only
// to match a supplier server-side, and arrives back here as `supplier_id`.
export const INVOICE_PREFILL_KEYS = ['invoice_number', 'invoice_date', 'due_date', 'amount', 'vat_amount', 'currency', 'payment_reference'];

/**
 * Builds the patch a document is allowed to apply to a form. Nothing else
 * writes a prefilled value, so this function IS the rule.
 *
 * THE RULE (Principle 2 — No Data Loss, Ever): a field is filled ONLY when it
 * is still empty, or — creating a new document — still holds the untouched
 * value the dialog opened with (this month's date, "SEK"). If the user typed
 * an amount and the document says something else, theirs stands.
 *
 * `pristine` is the form object the dialog opened with, or null when EDITING
 * an existing document: there, only genuinely empty fields are ever touched,
 * because a stored date or currency is data, not a default.
 *
 * Partial results are NORMAL, not failures. The extractor leaves a field out
 * rather than guess (KD-090) and is especially lossy on two-column PDF layouts
 * where a label's value sits in another column, so absent keys are the
 * ordinary case and nothing here treats a thin result as an error.
 */
export function buildPrefillPatch(form, extracted, keys, pristine) {
    const patch = {};
    for (const key of keys) {
        const incoming = extracted[key];
        if (incoming === undefined || incoming === null || incoming === '') continue;
        const current = form[key];
        const untouched =
            current === '' || current === null || current === undefined ||
            (pristine != null && current === pristine[key]);
        if (!untouched) continue;
        // Every one of these fields is a string in form state (the number
        // inputs included) — a raw float would make the controlled input
        // switch shape mid-edit.
        patch[key] = String(incoming);
    }

    // A currency the form cannot display is worse than no currency: the Select
    // would render blank and save an unlisted code.
    if (patch.currency !== undefined && !CURRENCIES.includes(patch.currency)) {
        delete patch.currency;
    }
    // The currency describes the amount. If the user typed the amount himself,
    // his currency choice stands — silently restating a hand-typed figure in
    // another currency changes what it means without touching it.
    if (patch.currency !== undefined && patch.amount === undefined) {
        delete patch.currency;
    }

    // Moms is a COMPONENT of Totalt, and the server rejects a document where
    // it is not (0 <= vat_amount <= amount). Mixing a typed figure with a read
    // one can break that pairing, and a prefilled value that fails the form's
    // own validation would be worse than no prefill at all — so whichever of
    // the two THIS patch contributed is dropped, never the user's own.
    const effAmount = parseFloat(patch.amount ?? form.amount);
    const effVat = parseFloat(patch.vat_amount ?? form.vat_amount);
    if (!Number.isNaN(effAmount) && !Number.isNaN(effVat) && effVat > effAmount) {
        if (patch.vat_amount !== undefined) delete patch.vat_amount;
        else delete patch.amount;
    }

    return patch;
}
